import { MenuItem } from 'app/frontend/base/components/EntityMenu'
import { hasAccessToContent } from 'app/frontend/base/components/ProtectedContent'
import Subheader from 'app/frontend/base/components/Subheader'
import { useGuild } from 'app/frontend/base/config/context/guild'
import { shouldDisplayTeamHousingPage } from 'app/frontend/base/helpers/housingPageDisplay'
import { useCustomDomain } from 'app/frontend/base/hooks/useCustomDomain'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { gameVersionHas } from 'app/frontend/types/custom/GameVersion'

export const useTeamMenuItems = (): {
  menuItems: MenuItem[]
  lootMenuItems: Omit<MenuItem, 'children'>[]
  eventsMenuItems: Omit<MenuItem, 'children'>[]
} => {
  const { customDomain } = useCustomDomain()
  const { t } = useTranslation()
  const { teamReference, currentTeam, guild } = useGuild()

  const mainRosterMemberCount = useMemo(
    () =>
      currentTeam?.members?.filter(
        (m) =>
          m.teamRank.player_type === 'main' && m.teamRank.roster_visibility,
      ).length ?? 0,
    [currentTeam?.members],
  )

  if (!teamReference) {
    return { menuItems: [], lootMenuItems: [], eventsMenuItems: [] }
  }

  const hasBaseAdminAccess = hasAccessToContent(
    'admin',
    'base',
    guild,
    teamReference,
  ).hasAccess
  const showHousing =
    gameVersionHas('housing') && shouldDisplayTeamHousingPage(guild)

  const lootMenuItems: Omit<MenuItem, 'children'>[] = !gameVersionHas('loot')
    ? []
    : [
        {
          name: t('dashboard.team.loot.personal'),
          path: `${teamReference.path}/loot/characters`,
          width: 104,
          section: 'loot',
          requirement: 'member',
        },
        {
          name: t('dashboard.team.loot.overview'),
          path: `${teamReference.path}/loot/overview`,
          width: 120,
          section: 'loot',
          requirement:
            currentTeam?.settings.wishlistOverviewVisibility.value ===
            'entire_team'
              ? 'member'
              : 'admin',
        },
        ...(currentTeam?.settings.tierPriorityEnabled.value
          ? [
              {
                name: t('dashboard.team.loot.tierPriorities'),
                path: `${teamReference.path}/loot/tier-status`,
                width: 110,
                section: 'loot' as const,
                requirement: 'member' as const,
              },
            ]
          : []),
        ...(currentTeam?.settings.trinketPriorityEnabled.value
          ? [
              {
                name: t('dashboard.team.loot.trinketStatus'),
                path: `${teamReference.path}/loot/trinket-status`,
                width: 120,
                section: 'loot' as const,
                requirement: 'member' as const,
              },
            ]
          : []),
        {
          name: t('dashboard.team.loot.history'),
          path: `${teamReference.path}/loot/history`,
          width: 120,
          section: 'loot',
          requirement:
            currentTeam?.settings.wishlistLootHistoryVisibility.value ===
            'entire_team'
              ? 'member'
              : 'admin',
        },
      ]
  const eventsMenuItems: Omit<MenuItem, 'children'>[] = [
    {
      name: t('shared.overview'),
      path: `${teamReference.path}/events`,
      exact: true,
      width: 100,
      section: 'events',
      requirement: 'member',
    },
    ...(gameVersionHas('raid_plans')
      ? ([
          {
            name: t('shared.plans'),
            path: `${teamReference.path}/events/plans`,
            width: 69,
            section: 'plans',
            requirement: 'member',
          },
          {
            name: t('dashboard.team.plans.default-setup.page-title'),
            path: `${teamReference.path}/events/default-setups`,
            width: 120,
            section: 'plans',
            requirement: 'admin',
          },
        ] as Omit<MenuItem, 'children'>[])
      : []),
    {
      name: 'Insights',
      path: `${teamReference.path}/events/insights`,
      width: 95,
      section: 'events',
      requirement: 'member',
    },
  ]

  const menuItems: MenuItem[] = [
    ...(customDomain?.headerConfig?.hideOverviewMenuItem
      ? []
      : ([
          {
            name: t('shared.overview'),
            path: `${teamReference.path}/`,
            exact: true,
            section: 'base',
            requirement: 'member',
            width: 94,
          },
        ] as MenuItem[])),
    ...(gameVersionHas('audit')
      ? ([
          {
            name: t('shared.audit'),
            path: `${teamReference.path}/audit`,
            section: 'audit',
            requirement: 'member',
            width: 66,
          },
        ] as MenuItem[])
      : []),
    {
      name: t('shared.events'),
      path: `${teamReference.path}/events`,
      section: 'events',
      requirement: 'member',
      width: 76,
      children: eventsMenuItems,
    },
    {
      name: (
        <div
          className={`flex items-center gap-1.5 md:w-[4.7rem] ${
            currentTeam?.members === undefined ? 'opacity-0' : 'opacity-100'
          }`}
        >
          <span>{t('shared.roster')}</span>
          <span className='text-muted-foreground text-xs max-md:hidden'>
            ({mainRosterMemberCount})
          </span>
        </div>
      ),
      path: `${teamReference.path}/roster`,
      section: 'roster',
      requirement: 'member',
      width: 75,
    },
    ...(gameVersionHas('loot')
      ? ([
          {
            name: t('shared.loot'),
            path: `${teamReference.path}/loot`,
            section: 'loot',
            requirement: 'member',
            width: 61,
            children: lootMenuItems,
          },
        ] as MenuItem[])
      : []),
    {
      name: t('shared.news'),
      path: `${teamReference.path}/news`,
      section: 'news',
      requirement: 'member',
      width: 69,
    },
    {
      name: (
        <div
          className={`flex items-center gap-1.5 md:w-[5.8rem] ${
            currentTeam?.members === undefined ? 'opacity-0' : 'opacity-100'
          }`}
        >
          <span>{t('shared.applicants')}</span>
          <span className='text-muted-foreground text-xs max-md:hidden'>
            ({currentTeam?.openApplicationIds?.length ?? 0})
          </span>
        </div>
      ),
      path: `${teamReference.path}/applicants`,
      section: 'applications',
      requirement: 'member',
      width: 102,
    },
    ...(showHousing
      ? ([
          {
            name: t('shared.housing'),
            path: `${teamReference.path}/housing`,
            section: 'base',
            requirement: 'member',
            width: 88,
          },
        ] as MenuItem[])
      : []),
    ...(!hasBaseAdminAccess && gameVersionHas('blizzard_api')
      ? ([
          {
            name: t('shared.premium'),
            path: `${teamReference.path}/premium`,
            section: 'base',
            requirement: 'member',
            width: 85,
          },
        ] as MenuItem[])
      : []),
    {
      name: t('shared.settings'),
      path: `${teamReference.path}/settings`,
      section: 'base',
      requirement: 'admin',
      alwaysVisible: true,
      width: 87,
    },
  ]

  return { eventsMenuItems, lootMenuItems, menuItems }
}

const TeamSubheader = () => {
  const { menuItems } = useTeamMenuItems()

  if (!menuItems.length) return null

  return <Subheader menuItems={menuItems} />
}

export default TeamSubheader
